import { useEffect, useState } from 'react';
import { fetchHealth, probeSocket, type ServerHealth, type SocketProbe } from './status';

type Probe<T> = { state: 'checking' } | { state: 'done'; value: T | null };

/**
 * The scaffold's page: which build is running, whether the server answers
 * over HTTP and whether the socket path answers. Everything on it is a fact
 * the deploy gate and the e2e spec can read. The shell (slice 05) replaces it.
 */
export function App() {
  const [health, setHealth] = useState<Probe<ServerHealth>>({ state: 'checking' });
  const [socket, setSocket] = useState<Probe<SocketProbe>>({ state: 'checking' });

  useEffect(() => {
    let live = true;
    fetchHealth().then((v) => live && setHealth({ state: 'done', value: v }));
    probeSocket().then((v) => live && setSocket({ state: 'done', value: v }));
    return () => {
      live = false;
    };
  }, []);

  return (
    <main className="min-h-screen bg-stone-950 text-stone-100 flex items-center justify-center p-6">
      <section className="max-w-lg w-full rounded-lg border border-stone-800 bg-stone-900 p-6 space-y-4">
        <h1 className="text-2xl font-semibold">The Dragon's Table VTT</h1>
        <p className="text-stone-400">An internal playtest build. A table is opened from the site's games tab.</p>
        <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm">
          <dt className="text-stone-400">Client build</dt>
          <dd data-testid="client-commit">{__VTT_COMMIT__}</dd>

          <dt className="text-stone-400">Server</dt>
          <dd data-testid="server-status">
            {health.state === 'checking' && 'checking…'}
            {health.state === 'done' && health.value === null && 'unreachable'}
            {health.state === 'done' && health.value !== null && (
              <>
                {health.value.ok ? 'ok' : 'not ok'} · build {health.value.commit} · database {health.value.db}
                {health.value.schema ? ` · schema ${health.value.schema}` : ''}
              </>
            )}
          </dd>

          <dt className="text-stone-400">Socket</dt>
          <dd data-testid="socket-status">
            {socket.state === 'checking' && 'connecting…'}
            {socket.state === 'done' && socket.value === 'reachable' && 'reachable · sign-in required'}
            {socket.state === 'done' && socket.value !== 'reachable' && 'no answer'}
          </dd>
        </dl>
      </section>
    </main>
  );
}
